import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Progress, Radio, Result, Spin, message } from 'antd';
import practiceClient from '../../client/PracticeClient';
import useTranslation from '../../hooks/useTranslation';
import './ExamRunPage.css';

/**
 * Пробный экзамен.
 *
 * От практики отличается тем, чего здесь нет. Нет проверки по нажатию: ответ уходит и
 * назад пути нет — иначе это не экзамен. Нет обратной связи по ходу: верный вариант не
 * подсвечивается, потому что экзамен меряет, а не учит, и подсказка сделала бы замер
 * бессмысленным. И нет влияния на расписание — об этом знает сервер, а здесь только
 * следствие: после экзамена слова приходят по тому же плану, что и до него.
 */
const ExamRunPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const exam = useSelector((state) => state.exam);

    const [questions, setQuestions] = useState([]);
    const [index, setIndex] = useState(0);
    const [selected, setSelected] = useState(new Set());
    const [isSending, setIsSending] = useState(false);
    const [result, setResult] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(null);
    const shownAt = useRef(Date.now());

    const examId = exam?.examId;

    useEffect(() => {
        if (!examId) {
            navigate('/learn');
            return;
        }
        setQuestions(exam.questions || []);
        if (exam.durationMinutes > 0) {
            setSecondsLeft(exam.durationMinutes * 60);
        }
    }, [examId, exam, navigate]);

    const finish = useCallback(async () => {
        try {
            setResult(await practiceClient.finishExam(examId));
        } catch (error) {
            console.error('Не удалось завершить экзамен:', error);
            message.error(t('exam.finishFailed'));
        }
    }, [examId, t]);

    // Время вышло — экзамен закрывается сам. Оставить его открытым значило бы, что
    // ограничение по времени не ограничение, а надпись.
    useEffect(() => {
        if (secondsLeft === null || result) {
            return undefined;
        }
        if (secondsLeft <= 0) {
            finish();
            return undefined;
        }
        const timer = setTimeout(() => setSecondsLeft((left) => left - 1), 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft, result, finish]);

    useEffect(() => {
        shownAt.current = Date.now();
        setSelected(new Set());
    }, [index]);

    if (!examId) {
        return null;
    }

    if (result) {
        const percent = result.questionCount > 0
            ? Math.round((result.correctCount / result.questionCount) * 100)
            : 0;
        return (
            <Result
                className="examRun__result"
                status={percent >= 60 ? 'success' : 'warning'}
                title={t('exam.score', { correct: result.correctCount, total: result.questionCount })}
                subTitle={t('exam.noEffect')}
                extra={[
                    <Button type="primary" key="back" onClick={() => navigate('/learn')}>
                        {t('exam.backToPractice')}
                    </Button>,
                ]}
            />
        );
    }

    const item = questions[index];
    if (!item) {
        return <Spin className="examRun__spin" />;
    }

    const isMultipleCorrect = item.answers.filter((option) => option.isCorrect).length > 1;

    const handleOptionChange = (event) => {
        if (isMultipleCorrect) {
            const next = new Set(selected);
            if (next.has(event.target.value)) {
                next.delete(event.target.value);
            } else {
                next.add(event.target.value);
            }
            setSelected(next);
        } else {
            setSelected(new Set([event.target.value]));
        }
    };

    const isChosenCorrect = () => {
        const correct = item.answers.filter((option) => option.isCorrect).map((option) => option.text);
        return correct.length === selected.size && correct.every((text) => selected.has(text));
    };

    const handleNext = async () => {
        setIsSending(true);
        try {
            const answered = await practiceClient.answerExam(examId, {
                itemId: item.id,
                correct: isChosenCorrect(),
                attempts: 1,
                optionCount: item.answers.length,
                responseMs: Date.now() - shownAt.current,
                answeredAt: new Date().toISOString(),
            });
            if (index + 1 >= questions.length) {
                setResult(await practiceClient.finishExam(examId));
            } else {
                setIndex(index + 1);
            }
            return answered;
        } catch (error) {
            console.error('Не удалось отправить ответ:', error);
            message.error(t('exam.answerFailed'));
            return null;
        } finally {
            setIsSending(false);
        }
    };

    const minutes = secondsLeft === null ? null : Math.floor(secondsLeft / 60);
    const seconds = secondsLeft === null ? null : String(secondsLeft % 60).padStart(2, '0');

    return (
        <div className="examRun">
            <div className="examRun__head">
                <span className="examRun__counter">
                    {t('exam.question', { current: index + 1, total: questions.length })}
                </span>
                {secondsLeft !== null && (
                    <span className={`examRun__timer${secondsLeft <= 60 ? ' examRun__timer--low' : ''}`}>
                        {minutes}:{seconds}
                    </span>
                )}
            </div>
            <Progress
                percent={Math.round((index / questions.length) * 100)}
                showInfo={false}
                size="small"
            />

            {/*
              * Вопрос рисуется здесь, а не карточкой практики. Та владеет кнопкой
              * «проверить» и показывает верный вариант — на экзамене подсказка сделала
              * бы замер бессмысленным, и переиспользовать её значило бы бороться с
              * компонентом, чья суть в обратной связи.
              */}
            <div className="examRun__question">{item.question}</div>

            <div className="examRun__options">
                {isMultipleCorrect
                    ? item.answers.map((option) => (
                        <Checkbox
                            key={option.text}
                            className="examRun__option"
                            value={option.text}
                            checked={selected.has(option.text)}
                            onChange={handleOptionChange}
                        >
                            {option.text}
                        </Checkbox>
                    ))
                    : item.answers.map((option) => (
                        <Radio
                            key={option.text}
                            className="examRun__option"
                            value={option.text}
                            checked={selected.has(option.text)}
                            onChange={handleOptionChange}
                        >
                            {option.text}
                        </Radio>
                    ))}
            </div>

            {isMultipleCorrect && (
                <div className="examRun__hint">{t('exam.multipleAnswers')}</div>
            )}

            <div className="examRun__actions">
                <Button
                    type="primary"
                    block
                    loading={isSending}
                    disabled={selected.size === 0}
                    onClick={handleNext}
                >
                    {index + 1 >= questions.length ? t('exam.finish') : t('exam.next')}
                </Button>
            </div>
        </div>
    );
};

export default ExamRunPage;
